package co.maytes.standupbot.dashboard.features.integrations

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.appcredentials.AnthropicAppCredentialsDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialPreviewDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialProviderDto
import co.maytes.standupbot.shared.dto.appcredentials.TestConnectionResultDto

sealed interface LoadState {
    data object Idle : LoadState
    data object Loading : LoadState
    data object Ready : LoadState
    data class Error(val error: ApiError) : LoadState
}

/** Per-provider in-flight action state for the Save / Test / Delete buttons. */
sealed interface PanelAction {
    data object Idle : PanelAction
    data object Saving : PanelAction
    data object Testing : PanelAction
    data object Validating : PanelAction
    data object Deleting : PanelAction
}

/**
 * Dashboard-side store for `/api/v1/workspaces/{id}/app-credentials`.
 *
 * Holds three things in Compose state:
 *   - `listState`         — overall loading / ready / error for the GET
 *   - `previews`          — per-provider [AppCredentialPreviewDto] (present + hints)
 *   - `panelActions`      — which per-provider mutation is currently in-flight
 *   - `lastTestResults`   — outcome of the most recent `Test connection` per provider
 *
 * Save + test flows keep their own coroutine — callers handle launching via
 * `rememberCoroutineScope().launch`.
 */
class AppCredentialsStore(private val api: ApiClient) {
    var listState: LoadState by mutableStateOf(LoadState.Idle)
        private set

    private val previewByProvider = mutableStateMapOf<AppCredentialProviderDto, AppCredentialPreviewDto>()
    private val panelActionByProvider = mutableStateMapOf<AppCredentialProviderDto, PanelAction>()
    private val testResultByProvider = mutableStateMapOf<AppCredentialProviderDto, TestConnectionResultDto>()

    fun preview(provider: AppCredentialProviderDto): AppCredentialPreviewDto? = previewByProvider[provider]

    fun panelAction(provider: AppCredentialProviderDto): PanelAction =
        panelActionByProvider[provider] ?: PanelAction.Idle

    fun lastTestResult(provider: AppCredentialProviderDto): TestConnectionResultDto? = testResultByProvider[provider]

    suspend fun refresh(workspaceId: String) {
        listState = LoadState.Loading
        when (val res = api.get<List<AppCredentialPreviewDto>>("/api/v1/workspaces/$workspaceId/app-credentials")) {
            is Result.Ok -> {
                previewByProvider.clear()
                res.value.forEach { previewByProvider[it.provider] = it }
                listState = LoadState.Ready
            }
            is Result.Err -> {
                listState = LoadState.Error(res.error)
            }
        }
    }

    suspend fun saveAnthropic(
        workspaceId: String,
        body: AnthropicAppCredentialsDto,
        verified: TestConnectionResultDto?,
    ): Result<AppCredentialPreviewDto, ApiError> =
        saveInternal(workspaceId, AppCredentialProviderDto.ANTHROPIC, body, verified)

    suspend fun validateAnthropic(
        workspaceId: String,
        body: AnthropicAppCredentialsDto,
    ): Result<TestConnectionResultDto, ApiError> {
        panelActionByProvider[AppCredentialProviderDto.ANTHROPIC] = PanelAction.Validating
        val res = api.post<AnthropicAppCredentialsDto, TestConnectionResultDto>(
            path = "/api/v1/workspaces/$workspaceId/app-credentials/anthropic/validate",
            body = body,
        )
        panelActionByProvider[AppCredentialProviderDto.ANTHROPIC] = PanelAction.Idle
        return res
    }

    private suspend inline fun <reified Body : Any> saveInternal(
        workspaceId: String,
        provider: AppCredentialProviderDto,
        body: Body,
        verified: TestConnectionResultDto?,
    ): Result<AppCredentialPreviewDto, ApiError> {
        panelActionByProvider[provider] = PanelAction.Saving
        val res = api.put<Body, AppCredentialPreviewDto>(
            path = "/api/v1/workspaces/$workspaceId/app-credentials/${provider.serialName()}",
            body = body,
        )
        panelActionByProvider[provider] = PanelAction.Idle
        if (res is Result.Ok) {
            previewByProvider[provider] = res.value
            if (verified != null) testResultByProvider[provider] = verified else testResultByProvider.remove(provider)
        }
        return res
    }

    suspend fun test(workspaceId: String, provider: AppCredentialProviderDto): Result<TestConnectionResultDto, ApiError> {
        panelActionByProvider[provider] = PanelAction.Testing
        val res = api.post<TestConnectionResultDto>(
            path = "/api/v1/workspaces/$workspaceId/app-credentials/${provider.serialName()}/test",
        )
        panelActionByProvider[provider] = PanelAction.Idle
        if (res is Result.Ok) {
            testResultByProvider[provider] = res.value
        }
        return res
    }

    suspend fun delete(workspaceId: String, provider: AppCredentialProviderDto): Result<Unit, ApiError> {
        panelActionByProvider[provider] = PanelAction.Deleting
        val res = api.delete<Unit>(
            path = "/api/v1/workspaces/$workspaceId/app-credentials/${provider.serialName()}",
        )
        panelActionByProvider[provider] = PanelAction.Idle
        if (res is Result.Ok) {
            previewByProvider.remove(provider)
            testResultByProvider.remove(provider)
        }
        return res
    }
}

@Composable
fun rememberAppCredentialsStore(): AppCredentialsStore {
    val api = rememberApiClient()
    return remember(api) { AppCredentialsStore(api) }
}

/** Backend expects lowercase provider names in path params. */
fun AppCredentialProviderDto.serialName(): String = when (this) {
    AppCredentialProviderDto.ANTHROPIC -> "anthropic"
}
